import { Component, Element, h, Host, Prop } from "@stencil/core";

import { setPropsIfNull } from "../../utils/utils";

/**
 * @slot primary - Slot for primary button
 * @slot secondary - Slot for secondary button
 * @slot tertiary - Slot for tertiary button
 */
@Component({
  tag: "stzh-actions",
  styleUrl: "stzh-actions.scss",
  shadow: true,
})
export class StzhActions {
  /** Variant determining what position the action buttons are */
  @Prop({ reflect: true }) variant: "form" | "dialog" | "process" | "search" = "form";

  /** Whether to split tertiary and primary/secondary from each other (left and right). */
  @Prop({ reflect: true }) spaceBetween: boolean = false;

  @Element() element: HTMLStzhActionsElement;

  private observer: MutationObserver;

  private init = () => {
    const secondaryButtons = this.element.querySelectorAll(
      'stzh-button[slot="secondary"], [slot="secondary"] stzh-button',
    );

    const tertiaryButtons = this.element.querySelectorAll(
      'stzh-button[slot="tertiary"], [slot="tertiary"] stzh-button',
    );

    secondaryButtons.forEach(button => {
      setPropsIfNull(button, {
        variant: "secondary",
      } as HTMLStzhButtonElement);
    });

    tertiaryButtons.forEach(button => {
      setPropsIfNull(button, {
        variant: "tertiary",
      } as HTMLStzhButtonElement);
    });
  };

  connectedCallback() {
    this.init();

    this.observer = new MutationObserver(this.init);
    this.observer.observe(this.element, {
      childList: true,
      subtree: true,
    });
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  render() {
    const classes = {
      "stzh-actions": true,
      "stzh-actions--space-between": this.spaceBetween,
      [`stzh-actions--${this.variant}`]: !!this.variant,
    };

    return (
      <Host space-between={this.spaceBetween}>
        <div class={classes}>
          <div class="stzh-actions__actions" role="group">
            <div class="stzh-actions__tertiary-actions">
              <slot name="tertiary"></slot>
            </div>
            <div class="stzh-actions__wrapper">
              <div class="stzh-actions__secondary-actions">
                <slot name="secondary"></slot>
              </div>
              <div class="stzh-actions__primary-actions">
                <slot name="primary"></slot>
              </div>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
